/**
 * Authentication Context
 *
 * Provides global authentication state management across the app.
 * Tracks user session and provides login/logout functionality.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { AuthSession } from '@/types';
import { logError } from '../services/logger';
import {
  getAuthSession,
  isSupabaseConfigured,
  onAuthStateChange,
  signOut as supabaseSignOut,
} from '@/config/supabase';
import { DEMO_MODE } from '@/config/demoConfig';

interface AuthContextValue {
  session: AuthSession | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Auth Provider Component
 *
 * Wraps the application to provide authentication state to all components.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);

  // Initial session fetch
  useEffect(() => {
    let mounted = true;
    let resolved = false;

    const applySession = (next: AuthSession | null) => {
      if (!mounted) return;
      resolved = true;
      setSession(next);
      setLoading(false);
    };

    // Safety net: never let the app hang on the loading screen. If nothing
    // resolves the session in time, fall through to signin instead of an
    // infinite "Loading...". Only fires while still unresolved, so it can never
    // clobber a real session that arrived first.
    const loadingTimeout = setTimeout(() => {
      if (mounted && !resolved) {
        logError(
          'AuthContext',
          'Auth resolution timed out; continuing unauthenticated',
          new Error('auth resolution timeout')
        );
        applySession(null);
      }
    }, 5000);

    if (DEMO_MODE) {
      // Demo mode never emits real auth events — resolve the synthetic session.
      getAuthSession()
        .then(applySession)
        .catch((error) => {
          logError('AuthContext', 'Error fetching demo auth session', error);
          applySession(null);
        });
      return () => {
        mounted = false;
        clearTimeout(loadingTimeout);
      };
    }

    // Real auth: rely solely on onAuthStateChange. It fires INITIAL_SESSION /
    // SIGNED_IN at startup with the recovered session (or null), then on every
    // auth change. We must NOT call getSession()/getUser() ourselves at mount —
    // that deadlocks the auth client's init lock and hangs on "Loading...".
    const subscription = onAuthStateChange((_event, newSession) =>
      applySession(newSession)
    );

    // If Supabase isn't configured, no auth event will ever fire.
    if (!isSupabaseConfigured()) applySession(null);

    return () => {
      mounted = false;
      clearTimeout(loadingTimeout);
      subscription?.unsubscribe();
    };
  }, []);

  const signOut = useCallback(async () => {
    await supabaseSignOut();
    setSession(null);
  }, []);

  const refreshSession = useCallback(async () => {
    const authSession = await getAuthSession();
    setSession(authSession);
  }, []);

  const value: AuthContextValue = {
    session,
    loading,
    signOut,
    refreshSession,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * useAuth Hook
 *
 * Access authentication state from any component within the AuthProvider.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { session, signOut, loading } = useAuth();
 *
 *   if (loading) return <div>Loading...</div>;
 *   if (!session) return <div>Please sign in</div>;
 *
 *   return (
 *     <div>
 *       Welcome, {session.user.email}
 *       <button onClick={signOut}>Sign Out</button>
 *     </div>
 *   );
 * }
 * ```
 */
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export default AuthContext;
